// ✅ src/pages/OrderManagement/ProjectEntryDocumentation.jsx
// FULL FILE — header-aware version with localStorage headers
//
// Backend mount:
//   app.use("/api/projectDocumentation", projectDocumentation);
//
// Endpoints used:
//   GET  https://nissi-india.com/api/projectDocumentation/grid?contractId=1&branchId=8&branchType=HO
//   GET  https://nissi-india.com/api/projectDocumentation/popupUrl?contractId=1&type=BOQ
//
// Popup pages:
//   /OrderManagement/ProjectRABillsdoc?contractId=1
//   /OrderManagement/ProjectBOQdoc?contractId=1
//   /OrderManagement/ProjectFinalBillsdoc?contractId=1
//   /OrderManagement/ProjectAttachmentsdoc?contractId=1

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FaFileInvoiceDollar,
  FaCalendarAlt,
  FaBuilding,
  FaSearch,
  FaFilter,
  FaDownload,
  FaPlus,
  FaPrint,
  FaExclamationTriangle,
  FaCheckCircle,
  FaBars,
  FaHome,
  FaChartBar,
  FaUsers,
  FaFileAlt,
  FaTasks,
  FaWarehouse,
  FaFilePdf,
  FaFileImage,
  FaFileWord,
  FaEye,
  FaSpinner,
} from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/projectDocumentation";

const toNum = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};

const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};

const money2 = (v) =>
  toNum(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const DocumentationComponent = () => {
  const location = useLocation();

  const [isDark, setIsDark] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenuItem, setActiveMenuItem] = useState("Documentation");

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  // ✅ localStorage helper
  const getLS = (key) => localStorage.getItem(key) || "";

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype");
  const userIdLS = getLS("userId") || getLS("loginuserid");

  // ✅ numeric values if needed
  const userId = useMemo(() => toInt(userIdLS || 0), [userIdLS]);
  const branchId = useMemo(() => toInt(branchIdLS || 0), [branchIdLS]);
  const branchType = useMemo(() => String(branchTypeLS || "").trim(), [branchTypeLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchIdLS,
      "x-branchtype": branchTypeLS,
      "x-userid": userIdLS,
    }),
    [finYearId, finYear, branchIdLS, branchTypeLS, userIdLS]
  );

  // ✅ contractId from URL or sessionStorage
  const contractId = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    const fromUrl = qs.get("contractId") || qs.get("contractid") || "";
    const fromSession =
      sessionStorage.getItem("Selcontractid") ||
      sessionStorage.getItem("selcontractid") ||
      sessionStorage.getItem("contractId") ||
      "";
    const cid = Number(fromUrl || fromSession || 0);
    return Number.isFinite(cid) ? cid : 0;
  }, [location.search]);

  // ✅ keep contractId in session
  useEffect(() => {
    if (contractId > 0) {
      sessionStorage.setItem("Selcontractid", String(contractId));
    }
  }, [contractId]);

  const withContractId = (path) => {
    if (!contractId) return path;
    const url = new URL(path, window.location.origin);
    url.searchParams.set("contractId", contractId);
    return url.pathname + url.search;
  };

  const sidebarItems = useMemo(
    () => [
      { name: "Date/LOA", icon: FaCalendarAlt, path: withContractId("/OrderManagement/ProjectEntry") },
      { name: "BOQ/Operative", icon: FaFileInvoiceDollar, path: withContractId("/OrderManagement/BOQ") },
      { name: "Program/DPR", icon: FaChartBar, path: withContractId("/OrderManagement/DailyProgressMonthlyWise") },
      { name: "Purchase Order", icon: FaFileAlt, path: withContractId("/OrderManagement/PurchaseOrderListing") },
      { name: "Rate Approval", icon: FaCheckCircle, path: withContractId("/OrderManagement/RateApprove") },
      { name: "Risk/Issues", icon: FaExclamationTriangle, path: withContractId("/OrderManagement/RiskAndIssue") },
      { name: "Documentation", icon: FaFileAlt, path: withContractId("/OrderManagement/ProjectEntryDocumentation") },
      { name: "Photos", icon: FaBuilding, path: withContractId("/OrderManagement/PhotoDocumentation") },
      { name: "Reconciliation", icon: FaTasks, path: withContractId("/OrderManagement/Reconciliation") },
      { name: "Do/Don't's", icon: FaWarehouse, path: withContractId("/OrderManagement/Rules") },
      { name: "Vendor Details", icon: FaUsers, path: withContractId("/OrderManagement/ProjectVendorDetails") },
    ],
    [contractId]
  );

  const handleMenuItemClick = (menuItem) => {
    setActiveMenuItem(menuItem.name);
  };

  // ✅ Load grid data
  const loadGrid = async () => {
    if (!contractId) {
      setRows([]);
      setErr("contractId missing in URL");
      return;
    }

    setLoading(true);
    setErr("");

    try {
      const res = await axios.get(`${API_BASE}/grid`, {
        params: { contractId, branchId, branchType, userId },
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        setRows([]);
        setErr(res.data?.message || "Failed to load");
        return;
      }

      const list = Array.isArray(res.data?.data) ? res.data.data : [];

      const mapped = list.map((x, idx) => ({
        sNo: idx + 1,
        contractId: Number(x.fld_contractid || 0),
        contractName: String(x.fld_contractname || ""),
        contractValue: toNum(x.fld_contractvalue),
      }));

      setRows(mapped);
    } catch (e) {
      console.error("Documentation grid error:", e);
      setRows([]);
      setErr(e?.response?.data?.message || e?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const moduleSelect =
      sessionStorage.getItem("ModuleSelect") ||
      sessionStorage.getItem("moduleSelect") ||
      "ERP";

    if (String(moduleSelect).toUpperCase() !== "ERP") {
      window.location.href = "/ModuleHomePage";
      return;
    }

    loadGrid();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, branchId, branchType, userId, commonHeaders]);

  const openPopup = (url) => {
    window.open(url, "_blank", "width=820,height=450,scrollbars=yes,resizable=no");
  };

  // ✅ popupUrl API with common headers
  const handleViewDocument = async (docType, cid) => {
    if (!cid) {
      alert("contractId missing");
      return;
    }

    const type = String(docType || "").trim().toUpperCase();
    if (!type) return;

    try {
      const res = await axios.get(`${API_BASE}/popupUrl`, {
        params: {
          contractId: cid,
          type,
          branchId,
          branchType,
          userId,
        },
        headers: commonHeaders,
      });

      if (res.data?.success && res.data?.url) {
        openPopup(res.data.url);
        return;
      }
    } catch (e) {
      console.warn("popupUrl api failed, using fallback route:", e?.message);
    }

    const map = {
      RABILLDETAILS: `/OrderManagement/ProjectRABillsdoc?contractId=${cid}`,
      BOQ: `/OrderManagement/ProjectBOQdoc?contractId=${cid}`,
      FINALBILLCOPY: `/OrderManagement/ProjectFinalBillsdoc?contractId=${cid}`,
      ATTACHMENTS: `/OrderManagement/ProjectAttachmentsdoc?contractId=${cid}`,
    };

    if (map[type]) openPopup(map[type]);
  };

  const ViewButton = ({ onClick }) => (
    <button
      onClick={onClick}
      className="px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded text-sm font-medium transition-colors duration-200 inline-flex items-center gap-2"
    >
      <FaEye />
      View
    </button>
  );

  const totalValue = useMemo(
    () => rows.reduce((sum, x) => sum + toNum(x.contractValue), 0),
    [rows]
  );

  return (
    <div className={`min-h-screen transition-all duration-300 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
      {/* Top Bar */}
      <div
        className={`sticky top-0 z-50 backdrop-blur-md border-b ${
          isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        } shadow-lg`}
      >
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? "hover:bg-gray-700 text-gray-300" : "hover:bg-gray-100 text-gray-600"
                }`}
              >
                <FaBars size={20} />
              </button>
              <div className="flex items-center gap-3">
                <FaHome className="text-blue-600 text-xl" />
                <span className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Project Management System
                </span>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsDark(!isDark)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? "bg-yellow-600 hover:bg-yellow-700 text-white" : "bg-gray-800 hover:bg-gray-900 text-white"
                }`}
              >
                {isDark ? "☀️" : "🌙"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div
          className={`${sidebarOpen ? "w-64" : "w-16"} transition-all duration-300 ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
          } border-r min-h-screen`}
        >
          <div className="p-4">
            {sidebarItems.map((item, index) => (
              <Link
                to={item.path}
                key={index}
                onClick={() => handleMenuItemClick(item)}
                className={`flex items-center gap-3 p-3 rounded-lg mb-2 cursor-pointer transition-all duration-200 ${
                  location.pathname === new URL(item.path, window.location.origin).pathname
                    ? isDark
                      ? "bg-blue-600 text-white"
                      : "bg-blue-500 text-white"
                    : isDark
                    ? "hover:bg-gray-700 text-gray-300"
                    : "hover:bg-gray-100 text-gray-700"
                }`}
              >
                <item.icon size={18} />
                {sidebarOpen && <span className="text-sm font-medium">{item.name}</span>}
              </Link>
            ))}
          </div>
        </div>

        {/* Main */}
        <div className="flex-1 min-w-0">
          <div
            className={`transition-all duration-300 min-h-screen ${
              isDark
                ? "bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900"
                : "bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"
            }`}
          >
            {/* Header */}
            <div
              className={`backdrop-blur-md border rounded-2xl m-6 mb-6 ${
                isDark ? "bg-gray-900/80 border-gray-700" : "bg-white/80 border-gray-200"
              }`}
            >
              <div className="px-6 py-4">
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
                  <div>
                    <h1 className={`text-2xl lg:text-3xl font-bold flex items-center gap-3 ${isDark ? "text-white" : "text-gray-900"}`}>
                      <FaFileAlt className="text-blue-500" />
                      DOCUMENTATION
                    </h1>
                    <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      Scanned Documents (RA Bills / BOQ / Final Bill Copy / Attachments)
                    </p>
                    <p className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      ContractId: {contractId || "-"} | BranchType: {branchType || "-"} | BranchId: {branchId || "-"} |
                      UserId: {userId || "-"} | Active Menu: {activeMenuItem}
                    </p>
                    {err ? (
                      <p className={`text-xs mt-1 ${isDark ? "text-red-300" : "text-red-600"}`}>{err}</p>
                    ) : null}
                    {loading ? (
                      <p className={`text-xs mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</p>
                    ) : null}
                  </div>

                  <div className="flex items-center gap-3 flex-wrap">
                    <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105">
                      <FaPlus />
                      Upload
                    </button>

                    <button className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105">
                      <FaDownload />
                      Export All
                    </button>

                    <button
                      onClick={() => window.print()}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105"
                    >
                      <FaPrint />
                      Print
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 pb-6">
              {/* Table */}
              <div
                className={`rounded-2xl shadow-2xl border overflow-hidden ${
                  isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"
                }`}
              >
                {/* status bar */}
                <div
                  className={`px-6 py-3 border-b ${
                    isDark ? "border-gray-700" : "border-gray-200"
                  } flex items-center justify-between`}
                >
                  <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    {loading ? "Loading..." : err ? `Error: ${err}` : "Ready"}
                  </div>

                  <button
                    onClick={loadGrid}
                    className="px-3 py-1 text-xs rounded bg-gray-200 hover:bg-gray-300 text-gray-800 inline-flex items-center gap-2"
                  >
                    {loading ? <FaSpinner className="animate-spin" /> : null}
                    Refresh
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-blue-600 text-white">
                      <tr>
                        <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider border-r border-blue-500">
                          Slno
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider border-r border-blue-500">
                          Contract Name
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider border-r border-blue-500">
                          Contract Value
                        </th>
                        <th
                          className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider"
                          style={{ backgroundColor: "#5a6fb8" }}
                        >
                          Scanned Documents
                        </th>
                      </tr>

                      <tr>
                        <th className="border-r border-blue-500" />
                        <th className="border-r border-blue-500" />
                        <th className="border-r border-blue-500" />
                        <th style={{ backgroundColor: "#5a6fb8" }}>
                          <div className="grid grid-cols-4 divide-x divide-blue-500">
                            <div className="px-2 py-2 text-center text-xs font-semibold uppercase">
                              RA Bills Details
                            </div>
                            <div className="px-2 py-2 text-center text-xs font-semibold uppercase">
                              BOQ
                            </div>
                            <div className="px-2 py-2 text-center text-xs font-semibold uppercase">
                              Final Bill Copy
                            </div>
                            <div className="px-2 py-2 text-center text-xs font-semibold uppercase">
                              Attachments
                            </div>
                          </div>
                        </th>
                      </tr>
                    </thead>

                    <tbody className={`${isDark ? "bg-gray-900/20" : "bg-white"} divide-y divide-gray-200`}>
                      {!loading && !rows.length ? (
                        <tr>
                          <td colSpan={4} className="px-6 py-10">
                            <div className="text-center">
                              <div className="text-lg font-bold text-blue-800">No Records Found</div>
                              <div className="text-sm text-gray-600 mt-1">
                                {err ? "Fix the error above and refresh." : "No documentation project row returned from API."}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        rows.map((item, index) => (
                          <tr
                            key={index}
                            className={`${isDark ? "hover:bg-gray-800/40" : "hover:bg-blue-50"} transition-colors duration-200`}
                          >
                            <td className="px-4 py-4 text-sm font-medium text-gray-900 text-center border-r border-gray-200">
                              {item.sNo}
                            </td>

                            <td className="px-4 py-4 text-sm text-blue-600 text-center border-r border-gray-200">
                              <span className="font-medium">{item.contractName}</span>
                            </td>

                            <td className="px-4 py-4 text-sm font-semibold text-green-600 text-center border-r border-gray-200">
                              {money2(item.contractValue)}
                            </td>

                            <td className="px-0">
                              <div className="grid grid-cols-4 divide-x divide-gray-200">
                                <div className="px-2 py-4 text-center">
                                  <ViewButton onClick={() => handleViewDocument("RABILLDETAILS", item.contractId)} />
                                </div>
                                <div className="px-2 py-4 text-center">
                                  <ViewButton onClick={() => handleViewDocument("BOQ", item.contractId)} />
                                </div>
                                <div className="px-2 py-4 text-center">
                                  <ViewButton onClick={() => handleViewDocument("FINALBILLCOPY", item.contractId)} />
                                </div>
                                <div className="px-2 py-4 text-center">
                                  <ViewButton onClick={() => handleViewDocument("ATTACHMENTS", item.contractId)} />
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Footer */}
                <div
                  className={`px-6 py-4 border-t ${
                    isDark ? "bg-gray-700/50 border-gray-600" : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      Total Records: {rows.length}
                    </div>
                    <div className={`text-sm font-semibold ${isDark ? "text-green-300" : "text-green-700"}`}>
                      Total Contract Value: ₹{money2(totalValue)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
                <div
                  className={`p-6 rounded-2xl shadow-xl border transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                    isDark
                      ? "bg-gradient-to-br from-blue-900/50 to-blue-800/30 backdrop-blur-md border-blue-700 text-white"
                      : "bg-gradient-to-br from-blue-500 to-blue-600 text-white border-blue-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm font-medium">RA Bills</p>
                      <p className="text-3xl font-bold">{rows.length}</p>
                    </div>
                    <FaFilePdf className="text-4xl text-blue-200" />
                  </div>
                </div>

                <div
                  className={`p-6 rounded-2xl shadow-xl border transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                    isDark
                      ? "bg-gradient-to-br from-green-900/50 to-green-800/30 backdrop-blur-md border-green-700 text-white"
                      : "bg-gradient-to-br from-green-500 to-green-600 text-white border-green-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-sm font-medium">BOQ Documents</p>
                      <p className="text-3xl font-bold">{rows.length}</p>
                    </div>
                    <FaFileWord className="text-4xl text-green-200" />
                  </div>
                </div>

                <div
                  className={`p-6 rounded-2xl shadow-xl border transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                    isDark
                      ? "bg-gradient-to-br from-purple-900/50 to-purple-800/30 backdrop-blur-md border-purple-700 text-white"
                      : "bg-gradient-to-br from-purple-500 to-purple-600 text-white border-purple-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm font-medium">Final Bills</p>
                      <p className="text-3xl font-bold">{rows.length}</p>
                    </div>
                    <FaFileInvoiceDollar className="text-4xl text-purple-200" />
                  </div>
                </div>

                <div
                  className={`p-6 rounded-2xl shadow-xl border transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                    isDark
                      ? "bg-gradient-to-br from-orange-900/50 to-orange-800/30 backdrop-blur-md border-orange-700 text-white"
                      : "bg-gradient-to-br from-orange-500 to-orange-600 text-white border-orange-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-100 text-sm font-medium">Attachments</p>
                      <p className="text-3xl font-bold">{rows.length}</p>
                    </div>
                    <FaFileImage className="text-4xl text-orange-200" />
                  </div>
                </div>
              </div>

              {/* Quick actions */}
              <div
                className={`mt-6 p-6 rounded-2xl shadow-xl border ${
                  isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"
                }`}
              >
                <h3 className={`text-lg font-bold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}>
                  Quick Actions
                </h3>

                <div className="flex flex-wrap gap-4">
                  <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-all duration-200">
                    <FaFileAlt /> View All Documents
                  </button>
                  <button className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 transition-all duration-200">
                    <FaDownload /> Bulk Download
                  </button>
                  <button className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-all duration-200">
                    <FaSearch /> Search Documents
                  </button>
                  <button className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg flex items-center gap-2 transition-all duration-200">
                    <FaFilter /> Filter by Type
                  </button>
                </div>
              </div>

              {/* Debug */}
              <div className="mt-4 text-xs text-gray-500">
                API: {API_BASE}/grid?contractId={contractId}&branchId={branchId}&branchType={encodeURIComponent(
                  branchType || ""
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentationComponent;